import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Checkbox, IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function ClientTable({
  name,
  rows,
  companies,
  organisations,
  activeFilter,
  orgLength,
  selectedDates,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  onUpdateDropDownData,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    ClientName: '',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'id':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };
  
      case 'ClientName':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    if (activeFilter) {
      setTableRows(rows.filter((row) => row.IsActive === true));
    } else {
      setTableRows(rows);
    }

    return () => {
      setTableRows([]);
    };
  }, [rows, activeFilter]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'id',
      'IsActive',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('ClientId') && item.hasOwnProperty('ClientName');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain ClientId and ClientName.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 200) {
          onUpdateDropDownData(name);
          resetReportingState();
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const clientColumns = useMemo(() => {
    let client_columns = [
      SelectColumn,
      {
        key: 'id',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client Id</b>
            {frozenColumns.indexOf('id') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'id')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'id')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        sortable: true,
        resizable: true,
        frozen: frozenColumns.indexOf('id') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'ClientName',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>First Name</b>
            {frozenColumns.indexOf('ClientName') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClientName')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClientName')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ClientName')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'ClientName')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ClientName}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ClientName: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ClientName') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'IsActive',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Active</b>
            {frozenColumns.indexOf('IsActive') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsActive') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive}
              onChange={(e) => {
                props.onRowChange(
                  {
                    ...props.row,
                    IsActive: e.target.checked,
                  },
                  true,
                );
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    if (orgLength.length > 1) {
      client_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      client_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = client_columns.filter((col) => col.frozen === true);
    let unfrozenCols = client_columns.filter((col) => col.frozen === false);
    client_columns = [...frozenCols, ...unfrozenCols];

    return client_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.ClientName
          ? r.ClientName?.toLowerCase().includes(
              filters.ClientName.toLowerCase(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      Client: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
    };

    export_value.Client.columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'ClientId', width: { wpx: 80 } },
      { title: 'ClientName', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
    ];
    tableRows.forEach((data) => {
      export_value.Client.data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.id ? Number(data.id) : -1,
          style: { numFmt: '0' },
        },
        { value: data.ClientName ? data.ClientName : '' },
        { value: data.IsActive ? 'true' : 'false' },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    return export_value;
  }, [tableRows, companies, organisations]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="Client" value={exportValue} />
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={clientColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

ClientTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  onUpdateDropDownData: PropTypes.func.isRequired,
};

export default ClientTable;
